import { InputDateModal } from "core/inputs/input-date/InputDateModal";
import InputText from "core/inputs/input-text/InputText";
import { FormattedMessage } from "react-intl";
import { Table } from "core/table/Table";
import { Typography } from "core/typography/Typography";
import { POPUP_STATUS, VAR_TYPES, MODAL_CONTENT } from "constants/constants";
import { phoneSize } from "utils/screenSizes";
import { useEffect, useState } from "react";
import {
  onAdd,
  onChange,
  onDelete,
  renderCommonBlock,
} from "utils/tableContentHelper";
import PropTypes from "prop-types";
import { headerGenerator } from "utils/HeaderHelper";
import { useMissionContext } from "api/mission/MissionProvider";
import AlertPopup from "components/alert-popup/AlertPopup";
import ModalFooter from "components/modal/ModalFooter";
import {
  ERROR_DATE_FEE_AFTER,
  // ERROR_DATE_FEE_BEFORE, // cf. HF-0053 in ManageDailyFee.js
  ERROR_FEE_MISSING_DATE,
  ERROR_FEE_NUM,
} from "constants/popup-codes";
import { RULES } from "constants/forms";
import { format } from "date-fns";

const ManageDailyFee = ({
  dailyFeeList,
  getUpdatedValues,
  lang,
  defaultCurrency,
  usersByIds,
  setElementsToDelete,
  onClose,
  onValidate,
  readOnly,
}) => {
  const {
    missionBySlug: { start_date, end_date },
  } = useMissionContext();

  const [reloadData, setReloadData] = useState(false);
  const [status, setStatus] = useState();
  const [code, setCode] = useState();

  const headerParams = [
    {
      key: "fee_value",
      defaultMessage: "Honoraire Journalier",
      icon: "salary",
      paddingLeft: ".75rem",
    },
    {
      key: "currency",
      defaultMessage: "Devise",
      icon: "salary",
      paddingLeft: ".75rem",
    },
    {
      key: "establishment_date",
      defaultMessage: "Date de prise en compte",
      icon: "calendar",
    },
    { key: "updated_by", defaultMessage: "Modifié par", icon: "user" },
    { key: "updated_at", defaultMessage: "le", icon: "calendar" },
  ];

  const header = headerGenerator(headerParams);

  const [data, setData] = useState(dailyFeeList);

  // cf. HF-0053 in ManageDailyFee.js
  // const isBeforeMissionStart = (establishment_date) =>
  //   new Date(establishment_date) < new Date(start_date);

  const isAfterMissionEnd = (establishment_date) =>
    new Date(establishment_date) > new Date(end_date);

  const isFeeValid = (num) => {
    return num > 0 && num.toString().match(RULES.daily_fee_pattern);
  };

  const setError = (code) => {
    setCode(code);
    setStatus(POPUP_STATUS.ERROR);
    setReloadData(!reloadData);
  };

  useEffect(() => {
    setData(
      dailyFeeList
        ?.map((elm) => ({
          ...elm,
          updated_by: usersByIds?.filter(
            (user) => user.id === elm.updated_by
          )[0]?.name,
        }))
        .sort(
          (a, b) =>
            new Date(b.establishment_date) - new Date(a.establishment_date)
        )
    );
  }, [dailyFeeList, usersByIds]);

  const checkDailyFee = (data) => {
    let error = null;

    data?.some((elm) => {
      if (!elm.fee_value || !elm.establishment_date) {
        error = ERROR_FEE_MISSING_DATE;
        return true;
      }
      /** TEMPORARY HOTFIX (HF-0053)
       *
       * When a Mission is created, a line is created in the ‘Manage Daily Fee’ modal with establishment date = start date.
       * If this start date is changed at a later date,
       * the “ERROR_DATE_FEE_AFTER” error is triggered every time a line is created or updated in the ‘Manage Daily Fee’ modal.
       *
       * DO NOT REMOVE this until the backend has synchronised ‘Manage Daily Fee’ when the mission start date is modified (estimated 20 July 2026)
       */

      // if (isBeforeMissionStart(elm.establishment_date)) {
      //   error = ERROR_DATE_FEE_BEFORE;
      //   return true;
      // }
      if (end_date && isAfterMissionEnd(elm.establishment_date)) {
        error = ERROR_DATE_FEE_AFTER;
        return true;
      }
      if (!isFeeValid(elm.fee_value)) {
        error = ERROR_FEE_NUM;
        return true;
      }
      return false;
    });

    return error;
  };

  const onClickValidate = () => {
    const error = checkDailyFee(data);
    if (error) {
      setError(error);
      return;
    }
    onValidate();
  };

  const formatContent = (content) =>
    content?.length > 0
      ? content?.map((elm, index) => {
          const dataTestIndex = content.length - 1 - index;
          return {
            id: elm?.id,
            fee_value: (
              <InputText
                value={elm.fee_value}
                type={VAR_TYPES.NUMBER}
                readOnly={readOnly}
                onChange={(val) =>
                  onChange({
                    getUpdatedValues,
                    id: elm?.id,
                    key: "fee_value",
                    setData,
                    data,
                    val: Number(val.target.value),
                  })
                }
                dataTestId={`${MODAL_CONTENT.MANAGE_DAILY_FEE}_modal-fee_value-${dataTestIndex}`}
              />
            ),
            currency: <div> {defaultCurrency}</div>,
            establishment_date: (
              <div
                data-testid={`${MODAL_CONTENT.MANAGE_DAILY_FEE}_modal-establishment_date-${dataTestIndex}`}
              >
                <InputDateModal
                  lang={lang}
                  elm={elm}
                  onChange={(date) => {
                    const formattedDate = date
                      ? format(date, "yyyy-MM-dd")
                      : "";
                    onChange({
                      getUpdatedValues,
                      id: elm?.id,
                      key: "establishment_date",
                      setData,
                      data,
                      val: formattedDate,
                    });
                  }}
                />
              </div>
            ),
            ...renderCommonBlock({
              data,
              getUpdatedValues,
              id: elm.id,
              onDelete,
              setData,
              updated_at: elm.updated_at,
              updated_by: elm.updated_by,
              lang,
              setElementsToDelete,
              readOnly: readOnly,
              deleteBtnTestId: `${MODAL_CONTENT.MANAGE_DAILY_FEE}_modal-delete_btn-${dataTestIndex}`,
            }),
          };
        })
      : [];

  return (
    <>
      <button
        onClick={() => onAdd({ header, data, setData })}
        className="button-style"
        data-testid={`${MODAL_CONTENT.MANAGE_DAILY_FEE}_modal-add_daily_fee_btn`}
      >
        <Typography
          className="link-1 violet"
          type="h2"
          label={
            <>
              +{" "}
              <FormattedMessage
                id="add_daily_fee"
                defaultMessage="Ajouter un honoraire journalier"
              />
            </>
          }
        />
      </button>
      <br />
      <div></div>
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <Table
        id="daily-fee"
        header={header}
        content={formatContent(data)}
        tableOverflow={"auto"}
        tableWidth={phoneSize ? "max-content" : "100%"}
      />
      <br />
      <br />
      <ModalFooter
        onClose={onClose}
        onValidate={() => onClickValidate()}
        modalType={MODAL_CONTENT.MANAGE_DAILY_FEE}
      />
    </>
  );
};

export default ManageDailyFee;

ManageDailyFee.propTypes = {
  dailyFeeList: PropTypes.array,
  getUpdatedValues: PropTypes.func,
  lang: PropTypes.string,
  defaultCurrency: PropTypes.string,
  usersByIds: PropTypes,
  setElementsToDelete: PropTypes.func,
  onClose: PropTypes.func,
  onValidate: PropTypes.func,
  readOnly: PropTypes.bool,
};
